import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import { useHistory } from "react-router-dom";
import {
    makeStyles,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    MenuItem,
    IconButton,
    Menu,
    Avatar,
    Tooltip,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import { brand, layout } from "../theme/tokens";
import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import FeedbackModal from "../components/FeedbackModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import withMediaToken from "../services/mediaUrl";
import api from "../services/api";
import AppointmentReminder from "../components/AppointmentReminder";

const drawerWidth = layout.drawerWidth;
const appBarHeight = layout.appBarHeight;

const useStyles = makeStyles((theme) => {
    const isDark = theme.palette.type === "dark";
    // Sidebar: navy da marca como âncora de chrome. Mantido escuro nos dois
    // modos de propósito — é o elemento que dá identidade ao produto.
    const sidebarBg = isDark ? brand.navyDeep : brand.navy;
    const sidebarBgTop = sidebarBg;
    const sidebarText = "#FDFDFD";
    const sidebarTextDim = "rgba(253,253,253,0.70)";
    const sidebarHover = "rgba(234,201,92,0.10)";
    const sidebarHoverText = brand.gold;
    const sidebarSelected = brand.goldDim;
    const sidebarDivider = "rgba(255,255,255,0.10)";
    // AppBar neutro. O gradiente azul que existia aqui era a assinatura visual
    // mais forte de "template de admin"; a barra vira superfície e a cor passa
    // a significar ação e estado, não decoração.
    const appBarText = theme.palette.text.primary;
    const appBarTextDim = theme.palette.text.secondary;
    return {
    root: {
        display: "flex",
        height: "100vh",
        [theme.breakpoints.down("sm")]: {
            height: "calc(100vh - 56px)",
        },
    },
    toolbar: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        minHeight: appBarHeight,
        gap: theme.spacing(1),
    },
    toolbarIcon: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        padding: "0 8px",
        minHeight: appBarHeight,
        background: "rgba(0,0,0,0.15)",
        borderBottom: `1px solid ${sidebarDivider}`,
        "& .MuiIconButton-root": {
            color: sidebarText,
        },
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        backgroundColor: theme.palette.background.paper,
        color: appBarText,
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: "none",
    },
    appBarShift: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    menuButton: {
        marginRight: theme.spacing(1),
        color: appBarTextDim,
    },
    menuButtonHidden: {
        display: "none",
    },
    brand: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.25),
        flexGrow: 1,
        minWidth: 0,
    },
    brandMark: {
        width: 32,
        height: 32,
        borderRadius: 8,
        background: sidebarBg,
        color: brand.gold,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 14,
        letterSpacing: 0.5,
    },
    title: {
        color: appBarText,
        fontWeight: 600,
        letterSpacing: 0.2,
        fontSize: "1rem",
        lineHeight: 1.1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    titleSub: {
        color: appBarTextDim,
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        lineHeight: 1,
    },
    actionsGroup: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
    },
    verticalDivider: {
        width: 1,
        height: 24,
        backgroundColor: theme.palette.divider,
        margin: theme.spacing(0, 1),
    },
    userButton: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(0.5, 1),
        borderRadius: 999,
        textTransform: "none",
        cursor: "pointer",
        border: "none",
        background: "transparent",
        color: appBarText,
        transition: theme.transitions.create(["background-color"]),
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        },
    },
    userAvatar: {
        width: 32,
        height: 32,
        fontSize: 14,
        fontWeight: 600,
        backgroundColor: sidebarBg,
        color: brand.gold,
    },
    userInfo: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        lineHeight: 1.1,
        [theme.breakpoints.down("xs")]: {
            display: "none",
        },
    },
    userName: {
        fontSize: "0.85rem",
        fontWeight: 600,
        color: appBarText,
        maxWidth: 140,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    userRole: {
        fontSize: "0.7rem",
        color: appBarTextDim,
        textTransform: "capitalize",
    },
    userMenuPaper: {
        marginTop: theme.spacing(1),
        minWidth: 200,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[6],
    },
    userMenuHeader: {
        padding: theme.spacing(1.5, 2),
        outline: "none",
    },
    menuItemIcon: {
        marginRight: theme.spacing(1.5),
        color: theme.palette.text.secondary,
        fontSize: 20,
    },
    drawerPaper: {
        position: "relative",
        whiteSpace: "nowrap",
        width: drawerWidth,
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        background: `linear-gradient(180deg, ${sidebarBgTop} 0%, ${sidebarBg} 100%)`,
        color: sidebarText,
        borderRight: "none",
        "& .MuiListItemIcon-root, & .MuiListItemIcon-root .MuiSvgIcon-root": {
            color: `${sidebarText} !important`,
            minWidth: 40,
        },
        "& .MuiListItemText-primary, & .MuiListItemText-secondary": {
            color: `${sidebarText} !important`,
            fontSize: "0.9rem",
        },
        "& .MuiListSubheader-root, & .MuiTypography-colorTextSecondary, & .MuiTypography-colorTextPrimary, & .MuiTypography-root, & .MuiTypography-body1, & .MuiTypography-body2": {
            color: `${sidebarText} !important`,
        },
        "& .MuiSvgIcon-root, & .MuiSvgIcon-colorAction, & .MuiSvgIcon-colorPrimary, & .MuiSvgIcon-colorSecondary": {
            color: `${sidebarText} !important`,
        },
        // Catch-all: qualquer svg/icone dentro do drawer (inclusive de
        // libs/badges/icons sem classe MuiSvgIcon) usa o currentColor do
        // texto da sidebar, evitando icones pretos isolados.
        "& svg": {
            color: `${sidebarText}`,
            fill: "currentColor",
        },
        "& .MuiIconButton-root": {
            color: `${sidebarText} !important`,
        },
        "& .MuiBadge-badge": {
            color: "#fff",
        },
        "& .MuiDivider-root": {
            backgroundColor: sidebarDivider,
        },
        "& .MuiListItem-button:hover": {
            backgroundColor: sidebarHover,
        },
        "& .MuiListItem-button:hover .MuiListItemIcon-root, & .MuiListItem-button:hover .MuiListItemText-primary, & .MuiListItem-button:hover .MuiSvgIcon-root, & .MuiListItem-button:hover .MuiTypography-root": {
            color: `${sidebarHoverText} !important`,
        },
        "& .Mui-selected, & .MuiListItem-button.Mui-selected": {
            backgroundColor: sidebarSelected,
            borderLeft: `3px solid ${sidebarHoverText}`,
            "&:hover": { backgroundColor: sidebarSelected },
        },
        "& .Mui-selected .MuiListItemIcon-root, & .Mui-selected .MuiListItemText-primary, & .Mui-selected .MuiSvgIcon-root, & .Mui-selected .MuiTypography-root": {
            color: `${sidebarHoverText} !important`,
            fontWeight: 600,
        },
        "& input": {
            color: sidebarText,
        },
        "& input::placeholder": {
            color: sidebarTextDim,
            opacity: 1,
        },
    },
    drawerPaperClose: {
        overflowX: "hidden",
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up("sm")]: {
            width: theme.spacing(9),
        },
    },
    appBarSpacer: {
        minHeight: appBarHeight,
    },
    // O espaçador da AppBar ocupa 60px DENTRO de `content`. Como as páginas
    // pedem `height: 100%`, o total dava 60px a mais que o container e
    // sobrava barra de rolagem na área inteira, fora do conteúdo da página.
    // Agora `content` é coluna flex: o espaçador é fixo e o miolo ocupa o
    // resto, então `height: 100%` da página resolve contra o espaço real.
    content: {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: theme.palette.background.default,
    },
    contentInner: {
        flex: 1,
        minHeight: 0,
        overflow: "auto",
    },
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    paper: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
    },
    iconButton: {
        color: appBarTextDim,
    },
    };
});

const LoggedInLayout = ({ children }) => {
    const classes = useStyles();
    const history = useHistory();
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const { handleLogout, loading, setUser } = useContext(AuthContext);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerVariant, setDrawerVariant] = useState("permanent");
    const { user } = useContext(AuthContext);
    const { darkMode, toggleTheme, setDarkMode } = useThemeContext();
    const userPrefsAppliedRef = React.useRef(false);

    // Aplica preferencia salva no User assim que carregar (uma vez)
    // Combinado com o default de abrir em telas desktop quando o user nao
    // tiver preferencia explicita.
    useEffect(() => {
        if (!user || userPrefsAppliedRef.current) return;
        const hasSidebarPref = typeof user.sidebarCollapsed === "boolean";
        const hasDarkPref = typeof user.darkMode === "boolean";
        if (!hasSidebarPref && !hasDarkPref && !user.id) {
            // ainda nao carregou de fato
            return;
        }
        userPrefsAppliedRef.current = true;
        if (hasSidebarPref) {
            setDrawerOpen(!user.sidebarCollapsed);
        } else if (document.body.offsetWidth > 600) {
            setDrawerOpen(true);
        }
        if (hasDarkPref && setDarkMode) {
            setDarkMode(user.darkMode);
        }
    }, [user, setDarkMode]);

    const persistPref = React.useCallback(async (patch) => {
        try {
            const { data } = await api.put("/profile", patch);
            if (setUser) setUser((prev) => ({ ...(prev || {}), ...data }));
        } catch (err) {
            // falha silenciosa — não atrapalha o toggle visual
        }
    }, [setUser]);

    const handleToggleDrawer = () => {
        const next = !drawerOpen;
        setDrawerOpen(next);
        if (user?.id) {
            persistPref({ sidebarCollapsed: !next });
        }
    };

    const handleToggleTheme = () => {
        toggleTheme();
        if (user?.id) {
            persistPref({ darkMode: !darkMode });
        }
    };

    const userAvatarSrc = user?.avatarUrl
        ? withMediaToken(user.avatarUrl)
        : undefined;

    useEffect(() => {
        if (document.body.offsetWidth < 600) {
            setDrawerVariant("temporary");
        } else {
            setDrawerVariant("permanent");
        }
    }, [drawerOpen]);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setMenuOpen(false);
    };

    const handleOpenUserModal = () => {
        handleCloseMenu();
        history.push("/profile");
    };

    const handleClickLogout = () => {
        handleCloseMenu();
        handleLogout();
    };

    const drawerClose = () => {
        if (document.body.offsetWidth < 600) {
            setDrawerOpen(false);
        }
    };

    if (loading) {
        return <BackdropLoading />;
    }

    return (
        <div className={classes.root}>
            {/* Aviso de agenda: montado aqui, e não na página da agenda,
                porque ele só serve se alcançar quem está em outra tela. */}
            <AppointmentReminder />
            <Drawer
                variant={drawerVariant}
                className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
                classes={{
                    paper: clsx(
                        classes.drawerPaper,
                        !drawerOpen && classes.drawerPaperClose
                    ),
                }}
                open={drawerOpen}
            >
                <div className={classes.toolbarIcon}>
                    <IconButton onClick={() => handleToggleDrawer()}>
                        <ChevronLeftIcon />
                    </IconButton>
                </div>
                <Divider />
                <List>
                    <MainListItems drawerClose={drawerClose} collapsed={!drawerOpen} />
                </List>
                <Divider />
            </Drawer>
            <UserModal
                open={userModalOpen}
                onClose={() => setUserModalOpen(false)}
                userId={user?.id}
            />
            <FeedbackModal
                open={feedbackOpen}
                onClose={() => setFeedbackOpen(false)}
            />
            <AppBar
                position="absolute"
                elevation={0}
                className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
            >
                <Toolbar className={classes.toolbar}>
                    <Tooltip title={i18n.t("mainDrawer.appBar.user.profile") ? "Menu" : "Menu"}>
                        <IconButton
                            edge="start"
                            aria-label="open drawer"
                            onClick={() => handleToggleDrawer()}
                            className={clsx(
                                classes.menuButton,
                                drawerOpen && classes.menuButtonHidden
                            )}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Tooltip>

                    <div className={classes.brand}>
                        <div className={classes.brandMark} aria-hidden="true">IM</div>
                        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                            <Typography
                                component="h1"
                                noWrap
                                className={classes.title}
                            >
                                InManager
                            </Typography>
                            <span className={classes.titleSub}>Workspace</span>
                        </div>
                    </div>

                    <div className={classes.actionsGroup}>
                        <Tooltip title={darkMode ? "Modo claro" : "Modo escuro"}>
                            <IconButton
                                onClick={handleToggleTheme}
                                className={classes.iconButton}
                                aria-label="toggle theme"
                            >
                                {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={i18n.t("feedback.tooltip")}>
                            <IconButton
                                onClick={() => setFeedbackOpen(true)}
                                className={classes.iconButton}
                                aria-label="report issue or improvement"
                            >
                                <HelpOutlineIcon />
                            </IconButton>
                        </Tooltip>

                        {user.id && (
                            <NotificationsPopOver className={classes.iconButton} />
                        )}

                        <div className={classes.verticalDivider} />

                        <button
                            type="button"
                            className={classes.userButton}
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenu}
                        >
                            <Avatar
                                src={userAvatarSrc}
                                className={classes.userAvatar}
                                alt={user?.name || "Usuário"}
                            >
                                {(user?.name || "U").trim().charAt(0).toUpperCase()}
                            </Avatar>
                            <div className={classes.userInfo}>
                                <span className={classes.userName}>
                                    {user?.name || "Usuário"}
                                </span>
                                <span className={classes.userRole}>
                                    {user?.profile || "user"}
                                </span>
                            </div>
                            <ExpandMoreIcon fontSize="small" style={{ opacity: 0.6 }} />
                        </button>

                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            getContentAnchorEl={null}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            open={menuOpen}
                            onClose={handleCloseMenu}
                            classes={{ paper: classes.userMenuPaper }}
                        >
                            <div className={classes.userMenuHeader}>
                                <Typography variant="subtitle2" noWrap>
                                    {user?.name || "Usuário"}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" noWrap>
                                    {user?.email || ""}
                                </Typography>
                            </div>
                            <Divider />
                            <MenuItem onClick={handleOpenUserModal}>
                                <PersonOutlineIcon className={classes.menuItemIcon} />
                                {i18n.t("mainDrawer.appBar.user.profile")}
                            </MenuItem>
                            <MenuItem onClick={handleClickLogout}>
                                <ExitToAppIcon className={classes.menuItemIcon} />
                                {i18n.t("mainDrawer.appBar.user.logout")}
                            </MenuItem>
                        </Menu>
                    </div>
                </Toolbar>
            </AppBar>
            <main className={classes.content}>
                <div className={classes.appBarSpacer} />
                <div className={classes.contentInner}>
                    {children ? children : null}
                </div>
            </main>
        </div>
    );
};

export default LoggedInLayout;